// Per-device habits for the Add Event editor — quietly shapes the
// form to the user's defaults after they've been using it long
// enough that we have signal.
//
// Tracks per-device, in localStorage:
//   • first_seen_at      ISO of the first Add Event open on this device
//   • opens              total number of Add Event opens
//   • expands            counts of how often the user manually expanded
//                        each optional section (endsAt / notes / linkedList)
//
// `getHabitDefaults()` returns each section's default-open state. We only
// flip a section to default-open after BOTH:
//   1. The device has been using the editor for at least RIPENING_DAYS
//      (7 by default — long enough to be reasonably sure the pattern is
//      a habit and not a one-off, no matter how often the user writes
//      events).
//   2. They've expanded that section in at least MIN_EXPANDS distinct
//      opens AND that's at least RATIO of all opens.
// Until then, every section starts collapsed.

const KEY = "famfeel_event_editor_habits_v1";
const SECTIONS = ["endsAt", "notes", "linkedList"];
const RIPENING_DAYS = 7;
const MIN_EXPANDS = 5;
const RATIO = 0.5;

function _load() {
  const empty = {
    first_seen_at: null,
    opens: 0,
    expands: { endsAt: 0, notes: 0, linkedList: 0 },
  };
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!raw || typeof raw !== "object") return empty;
    return {
      first_seen_at: raw.first_seen_at || null,
      opens: Number(raw.opens) || 0,
      expands: {
        endsAt: Number(raw.expands?.endsAt) || 0,
        notes: Number(raw.expands?.notes) || 0,
        linkedList: Number(raw.expands?.linkedList) || 0,
      },
    };
  } catch {
    return empty;
  }
}

function _save(h) {
  try {
    localStorage.setItem(KEY, JSON.stringify(h));
  } catch { /* private mode / quota — ignore */ }
}

// Call once per fresh "Add event" open. Edits of existing events
// don't count: the editor auto-expands sections that already have a
// value, so they'd inflate the expand counts artificially.
export function recordEditorOpen() {
  const h = _load();
  if (!h.first_seen_at) h.first_seen_at = new Date().toISOString();
  h.opens = (h.opens || 0) + 1;
  _save(h);
}

// Call when the user manually taps a "+ Ends / + Notes / + Linked list"
// chip. Increments the section's expand counter so a clear preference
// emerges over time.
export function recordSectionExpand(section) {
  if (!SECTIONS.includes(section)) return;
  const h = _load();
  h.expands[section] = (h.expands[section] || 0) + 1;
  _save(h);
}

// Returns { endsAt, notes, linkedList } booleans — the recommended
// default-open state for each section. Empty (all false) for the
// first ~week or until enough signal accumulates.
export function getHabitDefaults() {
  const out = { endsAt: false, notes: false, linkedList: false };
  const h = _load();
  if (!h.first_seen_at) return out;
  const ageMs = Date.now() - new Date(h.first_seen_at).getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  if (ageDays < RIPENING_DAYS) return out;
  const opens = h.opens || 0;
  // Need at least 2× MIN_EXPANDS opens before we trust the ratio
  // (otherwise a single "I always add notes" Tuesday tips it on).
  if (opens < MIN_EXPANDS * 2) return out;
  for (const s of SECTIONS) {
    const e = h.expands[s] || 0;
    if (e >= MIN_EXPANDS && e / opens >= RATIO) out[s] = true;
  }
  return out;
}
